import type {
  FormValues,
  KycDetails,
  OnboardingFieldRow,
} from '@/features/auth/types/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';
import { fieldKey, hasValue } from '@/features/auth/utils/onboardingFields';

export function buildPanRows(panDetails: KycDetails | null) {
  if (!panDetails) return [];
  return [
    {
      label: 'Name',
      value: panDetails.registered_name || panDetails.name_pan_card,
    },
    { label: 'PAN', value: panDetails.pan },
    { label: 'Type', value: panDetails.type },
    { label: 'Status', value: panDetails.pan_status },
    { label: "Father's Name", value: panDetails.father_name },
    { label: 'Aadhaar', value: panDetails.aadhaar_seeding_status_desc },
    {
      label: 'Name Match',
      value: panDetails.name_match_score
        ? `${panDetails.name_match_result || 'Matched'} (${panDetails.name_match_score}%)`
        : panDetails.name_match_result,
    },
  ];
}

export function buildGstRows(gstDetails: KycDetails | null) {
  if (!gstDetails) return [];
  return [
    { label: 'Legal Name', value: gstDetails.legal_name_of_business },
    { label: 'Date of Registration', value: gstDetails.date_of_registration },
    { label: 'Constitution', value: gstDetails.constitution_of_business },
  ];
}

export const isKycFieldName = (name: string) => {
  const key = fieldKey(name);
  return key === KYC.PAN || key === KYC.GST;
};

export function shouldShowVerifyButton(params: {
  name: string;
  currentValue: unknown;
  editingFields: Record<string, boolean>;
  verifiedValues: Record<string, string>;
  kycStatus?: { panVerified?: boolean; gstVerified?: boolean };
  savedDetails: FormValues;
}): boolean {
  const { name, currentValue, editingFields, verifiedValues, kycStatus, savedDetails } =
    params;
  const key = fieldKey(name);
  if (key !== KYC.PAN && key !== KYC.GST) return false;

  if (!hasValue(currentValue)) return false;
  if (editingFields[name]) return true;

  const verifiedValue = verifiedValues[name];
  if (verifiedValue !== undefined) return verifiedValue !== currentValue;

  const apiVerified =
    key === KYC.PAN ? kycStatus?.panVerified : kycStatus?.gstVerified;
  if (apiVerified) return currentValue !== savedDetails[name];

  return true;
}

export function isKycFieldVerified(params: {
  name: string;
  currentValue: unknown;
  editingFields: Record<string, boolean>;
  verifiedValues: Record<string, string>;
  kycStatus?: { panVerified?: boolean; gstVerified?: boolean };
  savedDetails: FormValues;
}): boolean {
  const { name, currentValue, editingFields, verifiedValues, kycStatus, savedDetails } =
    params;
  const key = fieldKey(name);
  if (key !== KYC.PAN && key !== KYC.GST) return true;

  if (!hasValue(currentValue)) return false;
  if (editingFields[name]) return false;

  const verifiedValue = verifiedValues[name];
  if (verifiedValue !== undefined) {
    return verifiedValue === currentValue;
  }

  const apiVerified =
    key === KYC.PAN ? kycStatus?.panVerified : kycStatus?.gstVerified;
  if (apiVerified && currentValue === savedDetails[name]) return true;

  return false;
}

export function isFieldLocked(params: {
  name: string;
  currentValue: unknown;
  gstDetails: KycDetails | null;
  isGstEditing: boolean;
  shouldShowVerify: boolean;
}): boolean {
  const { name, currentValue, gstDetails, isGstEditing, shouldShowVerify } =
    params;
  const key = fieldKey(name);
  if (key === KYC.COMPANY) {
    return Boolean(gstDetails?.legal_name_of_business) && !isGstEditing;
  }
  if (!isKycFieldName(name)) return false;
  return hasValue(currentValue) && !shouldShowVerify;
}

export function getPendingKycFieldLabels(
  onboardingFields: OnboardingFieldRow[],
  watchedValues: FormValues,
  isKycVerified: (name: string) => boolean,
): string[] {
  return onboardingFields
    .filter(({ field }) => {
      const key = fieldKey(field.fieldName);
      if (key !== KYC.PAN && key !== KYC.GST) return false;

      const value = watchedValues[field.fieldName];

      // GST is optional when empty — user can declare non-registration instead.
      if (key === KYC.GST && !hasValue(value)) return false;

      if (field.isRequired || hasValue(value)) {
        return !isKycVerified(field.fieldName);
      }
      return false;
    })
    .map(({ field }) => field.fieldLabel || field.fieldName);
}

export function getPendingVerifyMessage(pendingKycFields: string[]): string {
  if (!pendingKycFields.length) return '';
  if (pendingKycFields.length === 1) {
    return `Verify ${pendingKycFields[0]} (click Verify) before continuing.`;
  }
  const last = pendingKycFields[pendingKycFields.length - 1];
  const rest = pendingKycFields.slice(0, -1).join(', ');
  return `Verify ${rest} and ${last} (click Verify) before continuing.`;
}
